import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Privacy-aware analytics tracker.
 * - Fires a `/api/analytics/track` event on every route change
 * - Sends path, referrer, language, screen size and duration on previous page
 * - Heartbeats every 60s while the tab is visible so live presence stays accurate
 * - No raw IP storage on backend (fingerprint hash)
 */
export default function useAnalytics() {
  const loc = useLocation();
  const prevPath = useRef(null);
  const arrivedAt = useRef(Date.now());

  useEffect(() => {
    const now = Date.now();
    const duration = prevPath.current ? now - arrivedAt.current : null;
    const body = {
      path: loc.pathname + (loc.search || ""),
      referrer: prevPath.current ? `${window.location.origin}${prevPath.current}` : (document.referrer || ""),
      title: document.title,
      duration_ms: duration,
      screen: `${window.screen?.width || 0}x${window.screen?.height || 0}`,
      language: navigator.language || "en",
    };
    prevPath.current = body.path;
    arrivedAt.current = now;
    // Fire-and-forget
    axios.post(`${API}/analytics/track`, body).catch(() => {});
  }, [loc.pathname, loc.search]);

  // Heartbeat every 60s for live presence accuracy
  useEffect(() => {
    const beat = () => {
      if (document.visibilityState !== "visible") return;
      axios.post(`${API}/analytics/track`, {
        path: window.location.pathname,
        title: document.title,
        language: navigator.language || "en",
      }).catch(() => {});
    };
    const id = setInterval(beat, 60000);
    return () => clearInterval(id);
  }, []);
}
